import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormLabel,
	Grid,
	TextField,
} from '@mui/material';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useCreateBrokerComplianceStepMutation } from '@/features/complianceSteps/complianceStepsApi';
import validationSchema from './validationSchema';
import { PipelineStageSelect } from './PipelineStageSelect';

export const CreateComplianceStepDialog = () => {
	const dispatch = useDispatch();
	const open = useSelector((state) => state.dialog.open.createComplianceStep);
	const dialogData = useSelector((state) => state.dialog.data.createComplianceStep) ?? {};
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const brokerGroupId = dialogData?.brokerGroupId || userDetails?.brokerGroupId;
	const [createBrokerComplianceStep, { isLoading }] = useCreateBrokerComplianceStepMutation();

	const handleClose = (resetForm) => {
		resetForm();
		dispatch(closeDialog('createComplianceStep'));
	};

	return (
		<Formik
			initialValues={{
				pipelineId: dialogData?.pipelineId || '',
				name: '',
				description: '',
				stageId: '',
				isActive: true,
			}}
			validationSchema={validationSchema}
			onSubmit={async (values, { resetForm, setSubmitting }) => {
				const pipelineId = values.pipelineId;
				if (!brokerGroupId || !pipelineId) {
					dispatch(
						showToast({
							message:
								'Unable to create compliance step. Missing pipeline or broker data.',
							error: true,
						}),
					);
					setSubmitting(false);
					return;
				}

				try {
					await createBrokerComplianceStep({
						brokerGroupArray: userDetails?.brokerGroupArray,
						pipelineId,
						name: values.name,
						description: values.description,
						stage: values.stageId,
						isActive: values.isActive,
					}).unwrap();

					dispatch(
						showToast({
							message: 'Compliance step created successfully',
							success: true,
						}),
					);
					resetForm();
					dispatch(closeDialog('createComplianceStep'));
				} catch (error) {
					const message =
						error?.response?.data?.error?.message ??
						error?.message ??
						'There was an error creating the compliance step. Please try again.';

					dispatch(showToast({ message, error: true }));
				} finally {
					setSubmitting(false);
				}
			}}
		>
			{({ values, errors, touched, getFieldProps, isSubmitting, resetForm }) => (
				<Dialog open={open} onClose={() => handleClose(resetForm)} maxWidth='sm' fullWidth>
					<Form>
						<DialogTitle>Create Compliance Step</DialogTitle>
						<DialogContent sx={{ pt: 1 }}>
							<Grid container spacing={2} sx={{ mt: 1 }}>
								<Grid size={12}>
									<FormLabel required>Name</FormLabel>
									<TextField
										fullWidth
										multiline
										variant='outlined'
										{...getFieldProps('name')}
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										disabled={isSubmitting || isLoading}
									/>
								</Grid>

								<Grid size={12}>
									<FormLabel>Description</FormLabel>
									<TextField
										fullWidth
										multiline
										minRows={3}
										variant='outlined'
										{...getFieldProps('description')}
										disabled={isSubmitting || isLoading}
									/>
								</Grid>
								<PipelineStageSelect />
							</Grid>
						</DialogContent>
						<DialogActions>
							<Button
								onClick={() => handleClose(resetForm)}
								disabled={isSubmitting || isLoading}
							>
								Cancel
							</Button>
							<Button
								type='submit'
								variant='contained'
								disabled={isSubmitting || isLoading}
							>
								{isSubmitting || isLoading ? (
									<CircularProgress size='1.5em' sx={{ color: '#FFFFFF' }} />
								) : (
									'Create'
								)}
							</Button>
						</DialogActions>
					</Form>
				</Dialog>
			)}
		</Formik>
	);
};
